import {
  toWSDateString,
  useWSModalCustom,
  WSInfoBox,
  WSModal,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useMemo } from "react";
import { queriesExternalBankAccounts } from "@query";
import { selectorExternalBankAccountFullName } from "@selectors/selectorExternalBankAccountFullName";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useQueryMemberProfile,
  useQueryUserProfile
} from "../../../query/users/queries";
import { TransferBalance } from "../types";

type Props = {
  accountId: string;
  balance: TransferBalance;
  amount: number;
} & WSModalCustomProps<boolean | undefined>;

export const ModalAuthorizeACH: React.FC<Props> = ({
  accountId,
  balance,
  amount,
  ...modalProps
}) => {
  const queryExternalBankAccount =
    queriesExternalBankAccounts.getExternalBankAccount.useQuery(
      { bankAccountId: accountId },
      {
        enabled: !!accountId
      }
    );
  const externalBankAccount = queryExternalBankAccount.data;

  const userId = useUserId();

  const queryUser = useQueryUserProfile(userId);
  const queryMember = useQueryMemberProfile(userId);

  const name = useMemo(() => {
    if (!queryUser.data) {
      return "";
    }

    return wsName({
      user: queryUser.data,
      member: queryMember.data
    }).getResolvedName();
  }, [queryUser.data, queryMember.data]);

  const balanceName = useMemo(
    () => (balance === "wallet" ? "Wingspan Wallet" : "Tax Withholdings"),
    [balance]
  );

  return (
    <WSModal
      shimmer={queryExternalBankAccount.isLoading}
      title={`ACH Debit Authorization for Transfer to Your ${balanceName}`}
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: () => {
              modalProps.onClose(false);
            }
          },
          {
            label: "Authorize",
            onClick: () => {
              modalProps.onClose(true);
            }
          }
        ]
      }}
      {...modalProps}
    >
      <WSInfoBox>
        <p>
          I, <b>{name}</b>, authorize <b>Wingspan</b> (or its payment processor)
          to initiate a one-time Automated Clearing House (ACH) debit from my
          bank account (details below) for the purpose of transferring funds to
          my {balanceName}.
        </p>

        <p>
          Transfer Details:
          <ul>
            <li>
              <b>Amount to Transfer</b>:{" "}
              {formatMoney(amount, externalBankAccount?.currency || "USD")}
            </li>
            <li>
              <b>Transfer Date</b>: {toWSDateString(new Date(), "monthDayYear")}
            </li>
            <li>
              <b>From Bank Account</b>:{" "}
              {externalBankAccount
                ? selectorExternalBankAccountFullName(externalBankAccount)
                : ""}
            </li>
          </ul>
        </p>

        <p>
          Please review the following carefully:
          <ul>
            <li>
              <b>Single Transaction</b>: This authorization is for one ACH debit
              only.
            </li>
            <li>
              <b>Payment Finality</b>: Once you click "Authorize," this
              authorized payment may not be changed or cancelled through
              Wingspan.
            </li>
            <li>
              <b>Processing Times</b>: Payments authorized after 3:00 PM ET or
              on weekends/bank holidays are typically processed the next
              business day.
            </li>
            <li>
              <b>Record of Authorization</b>: You have the right to a copy of
              this authorization. Please print or save this screen for your
              records.
            </li>
            <li>
              <b>Returned Payments</b>: If this debit is returned unpaid by your
              bank (e.g., for insufficient funds), your bank may charge you a
              fee.
            </li>
            <li>
              <b>Legal Compliance</b>: I acknowledge that ACH transactions from
              my Bank Account must comply with U.S. law.
            </li>
          </ul>
        </p>

        <p>
          By clicking the "Authorize" button below, I confirm that:
          <ul>
            <li>
              <b>Authorized Signer</b>: I am an authorized signer on the Bank
              Account specified.
            </li>
            <li>
              <b>Terms of Agreement</b>: I have read, understood, and agree to
              all terms of this ACH Debit Authorization.
            </li>
            <li>
              <b>Authorization</b>: I authorize Wingspan to initiate this
              one-time ACH debit from my Bank Account to my {balanceName} as
              described.
            </li>
          </ul>
        </p>
      </WSInfoBox>
    </WSModal>
  );
};

export const useModalAuthorizeACH = () => useWSModalCustom(ModalAuthorizeACH);
